import { useMemo, useState } from 'react';
import { useApp, type View } from '../store';
import { howFar, openStep, readPolicy, stateOf, usesSlots, type SignoffState } from '../signoff-policy';
import { preparedSprintSignoffs } from '../documents';
import { can } from '../permissions';
import { decisionFromSignoff } from '../decisions';
import LogDecisionAction from './LogDecisionAction';
import Icon from './Icon';
import Expander from './Expander';
import { useTabKeys } from './useTabKeys';
import { displayName } from '../people';
import { relativeTime, asDate } from '../utils';
import { asTime } from '../change-request';
import { paymentsForApprover, type PaymentReview } from '../projects';
import { fmtMoney, fmtIn } from '../money';
import type { Signoff } from '../types';

/**
 * ── EVERYTHING WAITING TO BE APPROVED, IN ONE PLACE — #332 ──────────────────
 *
 * A sign-off is asked for where the thing is: on the finished sprint, on the
 * release, on the document. That is right, and on its own it recreates the
 * complaint this card exists to answer in a new shape — Vinnie's was that
 * sign-off was buried in Documents and *"it is never asked for"*. Asked for in
 * three places and listed in none, nobody can answer "what is outstanding".
 *
 * So both: the request appears where the work is, and this says what is open.
 *
 * ⚠️ IT IS NOT A PLACE TO SIGN. Every action opens the thing it is about,
 * because approving a fortnight without seeing what is in it is the signature
 * that makes a sign-off worthless. The row's "Sign off" button is a shortcut to
 * the thing, NOT a one-press approval on the list — it drills to the sprint, the
 * release, the document, where the signature is actually given having seen it.
 * This screen chases and points; the thing itself is answered.
 *
 * ── THE SHAPE VINNIE APPROVED (9 September 2026) ─────────────────────────────
 * A running total pinned at the head, true from every tab. Tabs one per kind, so
 * an uneven number in one kind never makes the layout lopsided. One window per
 * tab. It opens on the first tab that has something needing you. Settled is kept
 * per tab, collapsed to a line that opens. Rich rows: a progress ring, the
 * title with a Sign flag when it needs you, a line saying how far and who is
 * outstanding, and the one action that fits the state.
 */

type Group = {
  key: string;
  /* The subject's OWN id, apart from the key that groups by it — #350. The key
     carries the kind as well, and what the screen it opens needs is the id. */
  id: string;
  about: string;
  what: 'Sprint' | 'Release' | 'Document';
  askedAt?: number | null;
  rows: Signoff[];
  state: SignoffState;
  /* A deliverables change request drafted for a finished sprint and not yet
     sent — outstanding, but with nothing to sign until the developer sends it.
     Counts as waiting; reads differently, and opens the document to send it. */
  prepared?: boolean;
};

const ORDER: Record<SignoffState, number> = { rejected: 0, waiting: 1, approved: 2 };

/* A refusal that has been dealt with and cleared off the open list — migration
   0094. Kept only to be shown under settled, with who refused it and why, so a
   rejection is filed rather than vanishing. Documents and sprints only; a
   release is cleared by withdrawing it on the Releases screen. */
type Dismissed = {
  key: string;
  id: string;
  about: string;
  what: 'Sprint' | 'Document';
  why: string;
  refusedBy: string;
  dismissedBy: string;
  dismissedAt: number;
};

/* The tabs, in the order they sit across the top and the order the screen looks
   through them to decide which one to open on. The three kinds that write the
   `signoffs` table today; Phases join them once phase sign-off moves onto this
   ledger (S1). `goTo` is the screen the kind's subject is answered on. */
const KINDS: { k: string; label: string; what: Group['what']; goTo: View }[] = [
  { k: 'rel', label: 'Releases', what: 'Release', goTo: 'releases' },
  { k: 'spr', label: 'Sprints', what: 'Sprint', goTo: 'sprints' },
  { k: 'doc', label: 'Documents', what: 'Document', goTo: 'documents' },
];

/** A whole day is the unit a sign-off is chased in — nobody nudges by the hour. */
const dayOf = (ts: number, now: number): number => Math.floor((now - ts) / 86_400_000);
function agePhrase(askedAt: number | null | undefined, now: number): { text: string; old: boolean } {
  if (!askedAt) return { text: '', old: false };
  const d = dayOf(askedAt, now);
  if (d <= 0) return { text: 'today', old: false };
  return { text: d === 1 ? '1 day' : `${d} days`, old: d > 7 };
}

export default function Approvals() {
  const signoffs = useApp((s) => s.signoffs);
  const dismissedSignoffs = useApp((s) => s.dismissedSignoffs);
  const sprints = useApp((s) => s.sprints);
  const builds = useApp((s) => s.builds);
  const documents = useApp((s) => s.documents);
  const meta = useApp((s) => s.meta);
  const profiles = useApp((s) => s.profiles);
  const role = useApp((s) => s.role);
  const drillTo = useApp((s) => s.drillTo);
  const dismissDocument = useApp((s) => s.dismissDocument);
  const reRaiseDocument = useApp((s) => s.reRaiseDocument);
  const dismissSprintSignoff = useApp((s) => s.dismissSprintSignoff);
  const reRaiseSprintSignoff = useApp((s) => s.reRaiseSprintSignoff);
  const me = (useApp((s) => s.user?.email) ?? '').trim().toLowerCase();
  // For the Payments tab — the board's own lines, who may authorise, and the money.
  const projectId = useApp((s) => s.projectId);
  const project = useApp((s) => s.projects.find((p) => p.id === projectId));
  const signoffCover = useApp((s) => s.signoffCover);
  const phases = useApp((s) => s.phases);
  const milestones = useApp((s) => s.milestones);
  const base = useApp((s) => s.currency);

  /* Who may deal with a refusal — the same authority that asks for sign-off in
     the first place (Documents/Releases/Sprints all gate sending on this), which
     is exactly what the trigger in 0094 enforces: manager, admin or owner. */
  const mayClear = can(role, 'editSettings');
  /* Derive-first: a settled sign-off IS a decision, so a writer can log it with no
     retyping (mock 13). Writer-gated; the decisions RLS is the backstop. */
  const canSeed = can(role, 'editIssue');

  /* Null until somebody clicks a tab, then it sticks — so a manual choice is
     never yanked back to the default when the data updates underneath. */
  const [picked, setPicked] = useState<string | null>(null);
  const [openSettled, setOpenSettled] = useState<Record<string, boolean>>({});
  const [openDismissed, setOpenDismissed] = useState<Record<string, boolean>>({});
  /* The subject a clear is running on (its group key), and any refusal to show —
     so a button can say "working…" and an error lands where it happened. */
  const [busy, setBusy] = useState<string | null>(null);
  const [clearError, setClearError] = useState<Record<string, string>>({});

  const groups = useMemo<Group[]>(() => {
    const by = new Map<string, Signoff[]>();
    for (const r of signoffs) {
      /* The three are mutually exclusive in the database — see the check in
         0075 — so the first one set is the whole answer. */
      const key = r.sprintId
        ? `sprint:${r.sprintId}`
        : r.build != null
          ? `build:${r.build}`
          : r.documentId
            ? `doc:${r.documentId}`
            : '';
      if (!key) continue;
      (by.get(key) ?? by.set(key, []).get(key)!).push(r);
    }

    const out: Group[] = [];
    for (const [key, rows] of by) {
      const [kind, id] = [key.slice(0, key.indexOf(':')), key.slice(key.indexOf(':') + 1)];
      /*
       * ⚠️ A ROW WHOSE SUBJECT CANNOT BE FOUND IS STILL LISTED.
       *
       * The first draft did `if (!s) continue`, which is the kind of line that
       * reads as tidy and is a silent hole: anything this screen could not name
       * simply was not here, and the screen said "nothing has been sent for
       * approval" while a sheet sat waiting in the table. Found walking it on
       * 3 September 2026 — the sprint's own panel showed the approval and this
       * screen showed nothing, which is the worst pair of answers to get.
       *
       * A subject can genuinely be missing: a document superseded, a sprint
       * deleted, or simply not loaded yet on the first paint. Naming it "no
       * longer on this board" is honest and keeps the count right; dropping it
       * makes the screen lie about what is outstanding.
       */
      if (kind === 'sprint') {
        const s = sprints.find((x) => x.id === id);
        out.push({
          key, id, about: s?.name ?? 'A sprint no longer on this board', what: 'Sprint',
          askedAt: s?.signoffRequestedAt, rows, state: stateOf(rows),
        });
      } else if (kind === 'build') {
        const b = builds.find((x) => String(x.build) === id);
        /*
         * A WITHDRAWN OR ROLLED-BACK RELEASE IS OUT — not awaiting sign-off, so
         * it does not belong on the outstanding list. This is the opposite case
         * to the missing-subject one above: there, dropping a row would hide
         * something still open and make the screen lie; here the release is
         * settled by being pulled, so listing it is what would mislead.
         */
        if (b?.pulled) continue;
        out.push({
          key, id, about: b ? b.version || `Build ${b.build}` : 'A release no longer on this board',
          what: 'Release', askedAt: b?.signoffRequestedAt, rows, state: stateOf(rows),
        });
      } else {
        const d = documents.find((x) => x.id === id);
        out.push({
          key, id, about: d?.title ?? 'A document no longer on this board', what: 'Document',
          askedAt: d?.createdAt, rows, state: stateOf(rows),
        });
      }
    }
    /*
     * PREPARED BUT NOT SENT — the one-ledger flow's drafts. A slots sprint that
     * has been finished has a deliverables change request waiting on the
     * developer to send it; it has no signatures yet, so it is invisible to the
     * grouping above and would drop out of "what is outstanding" entirely. Added
     * here so a finished sprint keeps showing until it is actually signed off.
     * Only on a slots board — the rank model has no such draft.
     */
    if (usesSlots(readPolicy(meta.signoffPolicy)))
      for (const d of preparedSprintSignoffs(documents, sprints))
        out.push({
          key: `doc:${d.id}`, id: d.id, about: d.title, what: 'Document',
          askedAt: d.createdAt, rows: [], state: 'waiting', prepared: true,
        });

    /*
     * REFUSED FIRST, THEN WAITING, THEN APPROVED — and within each, the oldest
     * first. A refusal is the one that needs a decision from somebody today; a
     * thing waiting three weeks is the one being forgotten. Approved is kept on
     * the screen rather than hidden, because "it was approved on the ninth" is
     * the question this screen is asked most often once the chasing is done.
     */
    return out.sort(
      (a, b) => ORDER[a.state] - ORDER[b.state] || (a.askedAt ?? 0) - (b.askedAt ?? 0)
    );
  }, [signoffs, sprints, builds, documents, meta]);

  /* The refusals that have been dismissed — grouped by subject, newest first, so
     each tab can show its own under "settled" with the reason kept. Only the
     refused rows carry `dismissedAt` (the trigger stamps those and withdraws the
     rest), so these rows ARE the refusals and carry the `why`. */
  const dismissedGroups = useMemo<Dismissed[]>(() => {
    const by = new Map<string, Signoff[]>();
    for (const r of dismissedSignoffs) {
      const key = r.sprintId ? `sprint:${r.sprintId}` : r.documentId ? `doc:${r.documentId}` : '';
      if (!key) continue;
      (by.get(key) ?? by.set(key, []).get(key)!).push(r);
    }
    const out: Dismissed[] = [];
    for (const [key, rows] of by) {
      const [kind, id] = [key.slice(0, key.indexOf(':')), key.slice(key.indexOf(':') + 1)];
      const about =
        kind === 'sprint'
          ? (sprints.find((x) => x.id === id)?.name ?? 'A sprint no longer on this board')
          : (documents.find((x) => x.id === id)?.title ?? 'A document no longer on this board');
      const refusal = rows.find((r) => r.why) ?? rows[0];
      out.push({
        key,
        id,
        about,
        what: kind === 'sprint' ? 'Sprint' : 'Document',
        why: refusal?.why ?? '',
        refusedBy: refusal?.name ?? '',
        dismissedBy: rows.find((r) => r.dismissedBy)?.dismissedBy ?? '',
        dismissedAt: Math.max(...rows.map((r) => r.dismissedAt ?? 0)),
      });
    }
    return out.sort((a, b) => b.dismissedAt - a.dismissedAt);
  }, [dismissedSignoffs, sprints, documents]);

  /*
   * DOES THIS ONE NEED *YOU* RIGHT NOW — the thing the teal flag and dot mean.
   * You have a row on the sheet, unsigned, AND it is your turn: your step is the
   * one the chain is currently asking. A row waiting behind an earlier step is
   * outstanding but not yet yours, so it does not flag you. A prepared draft or
   * a settled sheet never needs a signature. Being named on the row (its
   * `signatoryEmail`) is what makes it yours — that is the permission, #332.
   */
  const needsMe = (g: Group): boolean => {
    if (g.prepared || g.state !== 'waiting' || !me) return false;
    const turn = openStep(g.rows);
    return g.rows.some(
      (r) =>
        !r.signedAt &&
        (r.signatoryEmail ?? '').trim().toLowerCase() === me &&
        (r.step ?? '') === (turn ?? '')
    );
  };

  const now = Date.now();

  /* THE PAYMENTS TAB — the money decisions routed to me on this board: pay-approvals
     and paid-line corrections. Not sign-off records, so sourced from the lines
     directly, but counted and shown exactly like the other kinds. */
  const isAdmin = can(role, 'editSettings');
  const payRows = useMemo<PaymentReview[]>(
    () =>
      project
        ? paymentsForApprover(project, {
            me,
            isAdmin,
            projectApprover: project.payApprover,
            hasActiveCoverFrom: (approver) => {
              const a = (approver ?? '').trim().toLowerCase();
              return (
                !!a &&
                signoffCover.some(
                  (c) => c.active && c.delegate === me && c.delegator === a && (c.until == null || c.until >= now),
                )
              );
            },
          })
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [project, me, isAdmin, signoffCover],
  );
  const payOpen = payRows.filter((r) => r.state !== 'approved');
  const paySettled = payRows.filter((r) => r.state === 'approved');
  const payNeedYou = payOpen.filter((r) => r.needsMe).length;
  const payRejected = payOpen.filter((r) => r.state === 'rejected').length;
  const payDot = payOpen.some((r) => r.state === 'rejected')
    ? 'is-bad'
    : payOpen.some((r) => r.needsMe)
      ? 'is-needs'
      : '';

  /* The running total, across every kind, pinned at the head. Open is
     everything not yet approved; of those, the ones that need you, the ones
     rejected, and the plain waiting remainder — three disjoint counts. */
  const signoffOpen = groups.filter((g) => g.state !== 'approved');
  const openCount = signoffOpen.length + payOpen.length;
  const needYou = signoffOpen.filter(needsMe).length + payNeedYou;
  const rejected = signoffOpen.filter((g) => g.state === 'rejected').length + payRejected;
  const waiting = openCount - needYou - rejected;

  const byKind = KINDS.map((kind) => {
    const items = groups.filter((g) => g.what === kind.what);
    const open = items.filter((g) => g.state !== 'approved');
    const settled = items.filter((g) => g.state === 'approved');
    const dismissed = dismissedGroups.filter((g) => g.what === kind.what);
    const dot = open.some((g) => g.state === 'rejected')
      ? 'is-bad'
      : open.some(needsMe)
        ? 'is-needs'
        : '';
    return { ...kind, items, open, settled, dismissed, dot };
  });

  /* Run a dismiss or re-raise on the subject the group is about, routed to the
     right per-kind action. One at a time; a refusal is shown against the row. */
  async function clear(g: Group, how: 'dismiss' | 'reraise') {
    if (busy) return;
    setBusy(g.key);
    setClearError((e) => ({ ...e, [g.key]: '' }));
    const run =
      g.what === 'Sprint'
        ? how === 'dismiss'
          ? dismissSprintSignoff
          : reRaiseSprintSignoff
        : how === 'dismiss'
          ? dismissDocument
          : reRaiseDocument;
    const problem = await run(g.id);
    setBusy(null);
    if (problem) setClearError((e) => ({ ...e, [g.key]: problem }));
  }

  /* Every tab's "does it need you / is it open / has anything" as plain booleans, so
     the preferred-tab choice can weigh the Payments tab beside the sign-off kinds
     without caring that its rows are a different shape. */
  const tabState = [
    ...byKind.map((t) => ({
      k: t.k,
      needs: t.open.some(needsMe),
      open: t.open.length > 0,
      any: t.items.length > 0 || t.dismissed.length > 0,
    })),
    { k: 'pay', needs: payNeedYou > 0, open: payOpen.length > 0, any: payRows.length > 0 },
  ];

  /* Open on the first tab that has something needing you; failing that the first
     tab with anything open; failing that the first tab that has anything at all
     (so an all-settled board lands on the kind that actually holds it, not an
     empty Releases); failing that the first tab. A click overrides it. */
  const preferred =
    tabState.find((t) => t.needs)?.k ??
    tabState.find((t) => t.open)?.k ??
    tabState.find((t) => t.any)?.k ??
    KINDS[0].k;
  const active = picked ?? preferred;
  const tab = byKind.find((t) => t.k === active) ?? byKind[0];
  const onPay = active === 'pay';
  // Arrow-key navigation for the tab strip — the sign-off kinds in order, then
  // the Payments tab when it is shown. `active` is already 'pay' on that tab.
  const tabOrder = [...byKind.map((t) => t.k), ...(payRows.length > 0 ? ['pay'] : [])];
  const tabKeys = useTabKeys(tabOrder, active, setPicked);

  if (groups.length === 0 && dismissedGroups.length === 0 && payRows.length === 0) {
    return (
      <div className="panel">
        {/* No panel heading — the view chrome already titles the screen "Sign-offs",
            and every other screen (Releases, Documents…) leaves the title to it
            rather than repeating it as a second heading. */}
        <p className="hint">
          Nothing is waiting for sign-off on this board yet. Sign-off is asked for where the work
          is — on a finished sprint, a release, or a document — and everything outstanding shows up
          here as it comes in.
        </p>
      </div>
    );
  }

  /* Who is still outstanding, said the way somebody chasing it needs to hear —
     "waiting on you" first, because that is the row you can do something about. */
  function outstandingLine(g: Group): string {
    const unsigned = g.rows.filter((r) => !r.signedAt);
    const names = unsigned.map((r) => r.name).filter(Boolean);
    if (needsMe(g)) {
      const others = names.length - 1;
      return others > 0 ? `you +${others} outstanding` : 'waiting on you';
    }
    const iSigned = !!me && g.rows.some((r) => (r.signatoryEmail ?? '').trim().toLowerCase() === me && r.signedAt);
    if (iSigned && names.length) return `you've signed · waiting on ${names.length}`;
    if (names.length === 0) return '';
    if (names.length <= 2) return names.join(', ');
    return `${names.slice(0, 2).join(', ')} +${names.length - 2}`;
  }

  function renderItem(g: Group) {
    const kind = KINDS.find((x) => x.what === g.what)!;
    const far = howFar(g.rows);
    const pct = far.of > 0 ? Math.round((far.signed / far.of) * 100) : 0;
    const mine = needsMe(g);
    const age = agePhrase(g.askedAt, now);

    /* THE AUDIT LINE, ON THE SCREEN ITSELF — Vinnie, 11 September 2026. Every
       answer that has actually been given, with who gave it and when to the
       minute. Only decisions are shown, never the people still to answer: those
       are the "waiting on" summary above, and printing a "Waiting" row each
       would drown the two lines that carry the record. */
    const decided = g.rows
      .filter((r) => r.signedAt)
      .sort((a, b) => (a.signedAt ?? 0) - (b.signedAt ?? 0));

    const ringClass = g.prepared
      ? 'is-zero'
      : g.state === 'rejected'
        ? 'is-rej'
        : far.of > 0 && far.signed === far.of
          ? 'is-full'
          : far.signed === 0
            ? 'is-zero'
            : '';

    /* The one action that fits — and every one of them OPENS the subject, never
       signs from here. "Sign off" is a shortcut to the thing to sign it having
       seen it; "Review" to the refusal; "Send" to the drafted document; "Remind"
       to where its approver link is re-sent. */
    const action = g.prepared
      ? { label: 'Send', primary: true }
      : g.state === 'rejected'
        ? { label: 'Review', primary: false }
        : mine
          ? { label: 'Sign off', primary: true }
          : { label: 'Remind', primary: false };

    const go = () => drillTo(kind.goTo, g.id);

    /* A refusal on a document or a sprint can be dealt with from here — cleared
       off the list (kept as settled with its reason) or sent out afresh. A
       release is cleared by withdrawing it on Releases, so it keeps just Review.
       Manager/owner only, matching who may ask for the sign-off. */
    const canClear =
      mayClear && g.state === 'rejected' && !g.prepared && (g.what === 'Document' || g.what === 'Sprint');
    const working = busy === g.key;
    const err = clearError[g.key];

    return (
      <li
        key={g.key}
        className={`ap-item is-${g.state}`}
        role="button"
        tabIndex={0}
        onClick={go}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(); }
        }}
      >
        <span
          className={`ap-ring ${ringClass}`}
          style={ringClass ? undefined : ({ ['--p']: pct } as React.CSSProperties)}
          aria-hidden="true"
        />
        <span className="ap-info">
          <span className="ap-t">
            {g.about}
            {mine && <span className="flag-loz">Sign</span>}
          </span>
          <span className="ap-sub">
            {g.prepared ? (
              <>Prepared — not sent for sign-off yet{age.text ? ` · drafted ${age.text} ago` : ''}</>
            ) : g.state === 'rejected' ? (
              <>
                <span className="is-no">✕ Not approved</span>
                {age.text ? ` · ${age.text}` : ''} · needs re-raising
              </>
            ) : (
              <>
                {far.signed} of {far.of} signed
                {age.text ? (
                  <> · <span className={age.old ? 'is-old' : undefined}>{age.text}</span></>
                ) : null}
                {outstandingLine(g) ? ` · ${outstandingLine(g)}` : ''}
              </>
            )}
          </span>
          {decided.length > 0 && (
            <ul className="ap-audit">
              {decided.map((r) => {
                const no = r.answer === 'rejected';
                return (
                  <li key={r.id} className={no ? 'is-no' : 'is-yes'}>
                    <span className="ap-audit-mark" aria-hidden="true">{no ? '✕' : '✓'}</span>
                    <span className="ap-audit-word">{no ? 'Rejected' : 'Approved'}</span>
                    <span className="ap-audit-who">
                      {r.name}
                      {r.onBehalfBy && (
                        <em className="ap-audit-behalf"> · recorded by {displayName(r.onBehalfBy, profiles)}</em>
                      )}
                    </span>
                    <span className="ap-audit-when">
                      {asDate(r.signedAt!)} · {asTime(r.signedAt!)}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
          {err && (
            <span className="ap-clear-error" role="alert">
              {err}
            </span>
          )}
        </span>
        <span className="ap-act">
          {canClear ? (
            <>
              {/* Deal with it: send it out again, or file the refusal away. Both
                  stop the row's own click so opening the subject stays a separate,
                  deliberate act (the row itself still opens it to Review). */}
              <button
                type="button"
                className="btn btn-primary"
                disabled={working}
                onClick={(e) => { e.stopPropagation(); void clear(g, 'reraise'); }}
              >
                {working ? 'Working…' : 'Re-raise'}
              </button>
              <button
                type="button"
                className="btn"
                disabled={working}
                onClick={(e) => { e.stopPropagation(); void clear(g, 'dismiss'); }}
              >
                Dismiss
              </button>
            </>
          ) : (
            <button
              type="button"
              className={action.primary ? 'btn btn-primary' : 'btn'}
              onClick={(e) => { e.stopPropagation(); go(); }}
            >
              {action.label}
            </button>
          )}
        </span>
      </li>
    );
  }

  /* A PAYMENTS-TAB ROW — a pay-approval or a paid-line correction routed to me.
     Reuses the sign-off row's shape (ring · info · action), says WHY it is mine, and
     — like every row here — only OPENS the line; the approve/reject happens there,
     where the approver sees the invoice first. */
  const faceMoney = (n: number, cur?: string) => (cur && cur !== base ? fmtIn(cur, n) : fmtMoney(n));
  const POOL_WORD: Record<string, string> = { capex: 'Capex', opex: 'Opex' };
  const changeWord = (field: 'pool' | 'phase' | 'milestone', v: string) =>
    field === 'pool'
      ? POOL_WORD[v] ?? v
      : field === 'milestone'
        ? v ? milestones.find((m) => m.id === v)?.name ?? 'a milestone' : 'Not against a milestone'
        : v ? phases.find((p) => p.id === v)?.name ?? 'Unassigned' : 'Unassigned';

  function whyMine(r: PaymentReview) {
    if (r.why === 'cover') return <>you&rsquo;re covering for <b>{displayName(r.coverFor ?? '', profiles)}</b></>;
    if (r.kind === 'correction') return <>you can approve this <b>(admin)</b> — you didn&rsquo;t request it</>;
    if (r.isException) return <>you&rsquo;re standing in <b>(admin)</b></>;
    return <>you are the designated approver</>;
  }

  function renderPayItem(r: PaymentReview) {
    const age = agePhrase(r.askedAt, now);
    const rejected = r.state === 'rejected';
    const title =
      r.kind === 'pay'
        ? `${r.vendor || 'Supplier'} ${faceMoney(r.amount, r.currency)} — approve for payment`
        : `${r.vendor || 'Line'} — correction on a paid line`;
    const go = () => drillTo('plan', `budget:${r.lineId}`);
    return (
      <li
        key={r.rowKey}
        className={`ap-item is-${rejected ? 'rejected' : 'waiting'}`}
        role="button"
        tabIndex={0}
        onClick={go}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(); } }}
      >
        <span className={`ap-ring ${rejected ? 'is-rej' : 'is-zero'}`} aria-hidden="true" />
        <span className="ap-info">
          <span className="ap-t">
            {title}
            {r.needsMe && <span className="flag-loz">Approve</span>}
            {r.isException && <span className="ap-exc-tag">⚠ Control exception</span>}
          </span>
          <span className="ap-sub">
            {rejected ? (
              <>
                <span className="is-no">✕ Not approved</span>
                {age.text ? ` · ${age.text}` : ''} · needs re-proposing by the requester
              </>
            ) : r.kind === 'pay' ? (
              <>
                Accepted &amp; ready to authorise · requested by {displayName(r.requestedBy, profiles)}
                {age.text ? ` · ${age.text}` : ''} · {whyMine(r)}
              </>
            ) : (
              <>
                Reclassify <b>{changeWord(r.change!.field, r.change!.from)}</b> →{' '}
                <b>{changeWord(r.change!.field, r.change!.to)}</b> · requested by{' '}
                {displayName(r.requestedBy, profiles)}
                {age.text ? ` · ${age.text}` : ''} · {whyMine(r)}
              </>
            )}
          </span>
          {rejected && r.settledBy && (
            <span className="ap-sub ap-audit">
              <span className="is-no">✕ Rejected</span> {displayName(r.settledBy, profiles)}
              {r.settledReason ? ` — “${r.settledReason}”` : ''}
              {r.settledAt ? ` · ${asDate(r.settledAt)} · ${asTime(r.settledAt)}` : ''}
            </span>
          )}
        </span>
        <span className="ap-act">
          <button
            type="button"
            className={rejected ? 'btn' : 'btn btn-primary'}
            onClick={(e) => { e.stopPropagation(); go(); }}
          >
            {rejected ? 'Review' : 'Review & approve'}
          </button>
        </span>
      </li>
    );
  }

  return (
    <div className="panel">
      {/* No panel heading — the view chrome titles the screen; see the note in the
          empty-state branch above. */}
      <p className="hint">
        Everything on this board that has been sent for sign-off. Open one to answer it — a sprint
        is signed off on the Sprints screen, a release on Releases, a document in Documents.
      </p>

      {/* Pinned, and true from whichever tab is open. */}
      <div className="ap-total">
        <div className="ap-big">
          {openCount}
          <div className="pa-heading">Open</div>
        </div>
        <div className="ap-split">
          <div className="ap-s is-you">Need you<b>{needYou}</b></div>
          <div className="ap-s">Waiting<b>{waiting}</b></div>
          <div className="ap-s is-rej">Rejected<b>{rejected}</b></div>
        </div>
      </div>

      <div className="ap-tabs" role="tablist" onKeyDown={tabKeys.onKeyDown}>
        {byKind.map((t) => (
          <button
            key={t.k}
            type="button"
            role="tab"
            aria-selected={t.k === active}
            ref={tabKeys.setRef(t.k)}
            tabIndex={tabKeys.tabIndex(t.k)}
            className={`ap-tab ${t.k === active ? 'is-on' : ''}`}
            onClick={() => setPicked(t.k)}
          >
            <span>{t.label}</span>
            <span className="ap-n">{t.open.length}</span>
            {t.dot && <span className={`ap-dot ${t.dot}`} aria-hidden="true" />}
          </button>
        ))}
        {/* The Payments tab — the money decisions routed to me, counted and badged
            like the sign-off kinds. Shown whenever there is anything to pay-approve
            or any correction on this board, settled ones included. */}
        {payRows.length > 0 && (
          <button
            type="button"
            role="tab"
            aria-selected={onPay}
            ref={tabKeys.setRef('pay')}
            tabIndex={tabKeys.tabIndex('pay')}
            className={`ap-tab ${onPay ? 'is-on' : ''}`}
            onClick={() => setPicked('pay')}
          >
            <span>Payments</span>
            <span className="ap-n">{payOpen.length}</span>
            {payDot && <span className={`ap-dot ${payDot}`} aria-hidden="true" />}
          </button>
        )}
      </div>

      <div className="ap-window">
        {onPay ? (
          payOpen.length === 0 ? (
            <p className="ap-empty">No payments need your decision right now.</p>
          ) : (
            <ul className="ap-list">{payOpen.map(renderPayItem)}</ul>
          )
        ) : tab.open.length === 0 ? (
          <p className="ap-empty">Nothing is waiting here.</p>
        ) : (
          <ul className="ap-list">{tab.open.map(renderItem)}</ul>
        )}

        {/* The Payments tab's settled block — corrections that were approved, kept
            as the record like the other kinds. (A rejected correction stays in the
            open list above, because it still needs re-proposing.) */}
        {onPay && paySettled.length > 0 && (
          <div className="ap-settled">
            <button
              type="button"
              className={`ap-settled-line${openSettled.pay ? ' is-open' : ''}`}
              aria-expanded={!!openSettled.pay}
              onClick={() => setOpenSettled((s) => ({ ...s, pay: !s.pay }))}
            >
              <span>
                <span className="ap-ok">✓ {paySettled.length} settled</span>
                {' — '}
                {paySettled.slice(0, 4).map((r) => r.vendor || 'Line').join(', ')}
                {paySettled.length > 4 ? `, +${paySettled.length - 4}` : ''}
              </span>
              <span className="ap-settled-chev" aria-hidden="true"><Icon name="chevron" size={18} /></span>
            </button>
            <Expander open={!!openSettled.pay}>
              <ul className="ap-settled-rows">
                {paySettled.map((r) => (
                  <li
                    key={r.rowKey}
                    role="button"
                    tabIndex={0}
                    onClick={() => drillTo('plan', `budget:${r.lineId}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); drillTo('plan', `budget:${r.lineId}`); }
                    }}
                  >
                    <span className="ap-ok" aria-hidden="true">✓</span>
                    <span>{r.vendor || 'Line'} — correction</span>
                    <span style={{ marginLeft: 'auto', color: 'var(--t3)' }}>
                      Approved{r.settledBy ? ` · ${displayName(r.settledBy, profiles)}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </Expander>
          </div>
        )}

        {!onPay && tab.settled.length > 0 && (
          <div className="ap-settled">
            <button
              type="button"
              className={`ap-settled-line${openSettled[tab.k] ? ' is-open' : ''}`}
              aria-expanded={!!openSettled[tab.k]}
              onClick={() => setOpenSettled((s) => ({ ...s, [tab.k]: !s[tab.k] }))}
            >
              <span>
                <span className="ap-ok">✓ {tab.settled.length} settled</span>
                {' — '}
                {tab.settled.slice(0, 4).map((g) => g.about).join(', ')}
                {tab.settled.length > 4 ? `, +${tab.settled.length - 4}` : ''}
              </span>
              <span className="ap-settled-chev" aria-hidden="true"><Icon name="chevron" size={18} /></span>
            </button>
            <Expander open={!!openSettled[tab.k]}>
              <ul className="ap-settled-rows">
                {tab.settled.map((g) => {
                  const far = howFar(g.rows);
                  return (
                    <li
                      key={g.key}
                      role="button"
                      tabIndex={0}
                      onClick={() => drillTo(KINDS.find((x) => x.what === g.what)!.goTo, g.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          drillTo(KINDS.find((x) => x.what === g.what)!.goTo, g.id);
                        }
                      }}
                    >
                      <span className="ap-ok" aria-hidden="true">✓</span>
                      <span>{g.about}</span>
                      <span style={{ marginLeft: 'auto', color: 'var(--t3)' }}>
                        Approved · all {far.of}
                      </span>
                      {/* DERIVE-FIRST SEED (B) — the approval IS the decision, so a
                          writer can log it from here with no retyping (mock 13). A
                          quiet secondary in the row's own action area; stop the
                          click bubbling so it doesn't also open the subject. */}
                      {canSeed && (
                        <span
                          className="ap-act"
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => e.stopPropagation()}
                        >
                          <LogDecisionAction
                            label="Log as a decision"
                            note="Carries the sign-off reason"
                            draft={decisionFromSignoff({
                              signoffId: g.id,
                              title: g.about,
                              why: g.rows.find((r) => r.why)?.why,
                              kindLabel: g.what,
                            })}
                          />
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </Expander>
          </div>
        )}

        {/* DISMISSED REFUSALS — kept, not erased (migration 0094). Collapsed to a
            line like the approved settled block; opened, each shows who refused
            it and why, and can be sent out again. */}
        {!onPay && tab.dismissed.length > 0 && (
          <div className="ap-settled ap-dismissed">
            <button
              type="button"
              className={`ap-settled-line${openDismissed[tab.k] ? ' is-open' : ''}`}
              aria-expanded={!!openDismissed[tab.k]}
              onClick={() => setOpenDismissed((s) => ({ ...s, [tab.k]: !s[tab.k] }))}
            >
              <span>
                <span className="ap-no">✕ {tab.dismissed.length} dismissed</span>
                {' — '}
                {tab.dismissed.slice(0, 4).map((g) => g.about).join(', ')}
                {tab.dismissed.length > 4 ? `, +${tab.dismissed.length - 4}` : ''}
              </span>
              <span className="ap-settled-chev" aria-hidden="true"><Icon name="chevron" size={18} /></span>
            </button>
            <Expander open={!!openDismissed[tab.k]}>
              <ul className="ap-settled-rows ap-dismissed-rows">
                {tab.dismissed.map((g) => (
                  <li
                    key={g.key}
                    role="button"
                    tabIndex={0}
                    onClick={() => drillTo(KINDS.find((x) => x.what === g.what)!.goTo, g.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        drillTo(KINDS.find((x) => x.what === g.what)!.goTo, g.id);
                      }
                    }}
                  >
                    <span className="ap-no" aria-hidden="true">✕</span>
                    <span className="ap-dismissed-info">
                      <span className="ap-dismissed-about">{g.about}</span>
                      {/* The reason the refusal existed, kept where it can be read
                          — the whole point of dismissing rather than deleting. */}
                      {g.why && (
                        <span className="ap-dismissed-why">
                          “{g.why}”{g.refusedBy ? ` — ${g.refusedBy}` : ''}
                        </span>
                      )}
                    </span>
                    <span className="ap-dismissed-when">
                      Dismissed
                      {g.dismissedBy ? ` by ${displayName(g.dismissedBy, profiles)}` : ''}
                      {g.dismissedAt ? ` · ${relativeTime(g.dismissedAt)}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </Expander>
          </div>
        )}
      </div>
    </div>
  );
}
